import { useState, useEffect } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import usePageTitle from "../hooks/usePageTitle";
import axios from "axios";
import { format } from "date-fns";
import {
  CheckCircle,
  XCircle,
  User,
  Calendar,
  CreditCard,
  Loader2,
  Scan,
  RotateCcw
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ValidatePage = () => {
  usePageTitle('Validate Membership', 'Verify Advanta Plus membership');
  const { membershipId, userId, code: pathCode } = useParams();
  const [searchParams] = useSearchParams();
  const queryCode = searchParams.get("code");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let qrData = "";
    if (membershipId && userId) {
      // Clean URL format: /validate/membershipId/userId
      qrData = `ADVANTA:${membershipId}:${userId}`;
    } else if (queryCode) {
      // Query param format: ?code=membershipId~userId
      const parts = queryCode.split("~");
      if (parts.length === 2) {
        qrData = `ADVANTA:${parts[0]}:${parts[1]}`;
      } else {
        qrData = `ADVANTA:${queryCode}`;
      }
    } else if (pathCode) {
      qrData = decodeURIComponent(pathCode);
    }

    if (!qrData) {
      setResult({ valid: false, message: "No membership code provided" });
      setLoading(false);
      return;
    }

    const validate = async () => {
      try {
        const response = await axios.post(`${API}/membership/validate`, {
          qr_data: qrData
        });
        setResult(response.data);
      } catch (error) {
        setResult({
          valid: false,
          message: error.response?.data?.detail || error.response?.data?.message || "Validation failed"
        });
      } finally {
        setLoading(false);
      }
    };

    validate();
  }, [pathCode, queryCode]);

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Navigation */}
      <nav className="bg-[#0B0F19] border-b border-[#D4AF37]/10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#B8860B] flex items-center justify-center shadow-lg">
              <span className="text-[#020617] font-bold text-lg tracking-tight">A+</span>
            </div>
            <span className="font-['Playfair_Display'] text-2xl text-white">Advanta <span className="text-[#D4AF37]">Plus</span></span>
          </Link>
          <span className="text-[#D4AF37] text-sm font-medium">Membership Check</span>
        </div>
      </nav>

      <div className="max-w-lg mx-auto px-6 py-8">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-3">
            <Scan className="w-7 h-7 text-[#D4AF37]" />
          </div>
          <h1 className="font-['Playfair_Display'] text-2xl text-white mb-1">
            Membership Validation
          </h1>
        </div>

        {/* Loading */}
        {loading && (
          <div className="bg-[#0B0F19] rounded-2xl p-10 border border-[#D4AF37]/10 text-center">
            <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin mx-auto mb-3" />
            <p className="text-white/70 text-sm">Validating membership...</p>
          </div>
        )}

        {/* Result */}
        {result && !loading && (
          <div className={`rounded-2xl p-6 border ${result.valid ? 'bg-[#047857]/10 border-[#047857]/30' : 'bg-red-500/10 border-red-500/30'}`}>
            <div className="text-center mb-5">
              {result.valid ? (
                <div className="w-16 h-16 rounded-full bg-[#047857]/20 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-8 h-8 text-[#047857]" />
                </div>
              ) : (
                <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-3">
                  <XCircle className="w-8 h-8 text-red-400" />
                </div>
              )}

              <h2 className={`font-['Playfair_Display'] text-xl ${result.valid ? 'text-[#047857]' : 'text-red-400'}`}>
                {result.valid ? 'Valid Membership' : 'Invalid'}
              </h2>

              {!result.valid && (
                <p className="text-red-400/70 text-sm mt-2">{result.message}</p>
              )}
            </div>

            {result.valid && result.user && (
              <div className="space-y-3">
                <div className="bg-white/5 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                      <User className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-white font-medium">
                        {result.user.first_name} {result.user.last_name}
                      </p>
                      <p className="text-white/50 text-xs">{result.user.email}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/5 rounded-xl p-3">
                    <div className="flex items-center gap-1 mb-1">
                      <CreditCard className="w-3 h-3 text-[#D4AF37]" />
                      <span className="text-white/50 text-xs">Plan</span>
                    </div>
                    <p className="text-white text-sm font-medium">
                      {result.membership.plan_name}
                    </p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-3">
                    <div className="flex items-center gap-1 mb-1">
                      <Calendar className="w-3 h-3 text-[#D4AF37]" />
                      <span className="text-white/50 text-xs">Expires</span>
                    </div>
                    <p className="text-white text-sm font-medium">
                      {format(new Date(result.membership.expiry_date), "MMM d, yyyy")}
                    </p>
                  </div>
                </div>

                {result.source && (
                  <div className="text-center pt-2">
                    <span className={`text-xs px-2 py-1 rounded ${result.source === 'wallet_pass' ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'}`}>
                      {result.source === 'wallet_pass' ? 'Wallet Pass' : 'Live App'}
                    </span>
                  </div>
                )}

                {result.verified_at && (
                  <div className="text-center pt-3 border-t border-white/10">
                    <p className="text-white/40 text-xs">
                      Verified {format(new Date(result.verified_at), "HH:mm:ss")}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Re-validate or scan another */}
            <div className="mt-5 flex gap-3">
              <button
                onClick={() => { setLoading(true); setResult(null); window.location.reload(); }}
                className="flex-1 flex items-center justify-center gap-2 bg-white/5 border border-white/10 rounded-xl py-3 text-white/70 hover:text-white hover:border-white/20 transition-colors text-sm"
              >
                <RotateCcw className="w-4 h-4" />
                Re-check
              </button>
              <Link
                to="/scan"
                className="flex-1 flex items-center justify-center gap-2 bg-[#D4AF37] rounded-xl py-3 text-[#020617] font-semibold text-sm hover:bg-[#B8860B] transition-colors"
              >
                <Scan className="w-4 h-4" />
                Open Scanner
              </Link>
            </div>
          </div>
        )}

        <div className="mt-6 text-center">
          <p className="text-white/40 text-xs">
            Advanta Plus Membership Verification
          </p>
        </div>
      </div>
    </div>
  );
};

export default ValidatePage;
